// Blocks → email-safe HTML (spec §2.4). Pure. A port of the prototype's ED_R
// renderers (breadstack-campaign-builder-final.html L1472–1628) to
// table-based, inline-styled markup: no flex / grid / aspect-ratio /
// position, no scripts or event handlers. Per-recipient content is emitted as
// `bs:if` / `bs:cart_items` markers that the backend evaluates at send time.
//
// Marker nesting: the backend's marker regex is non-nested by contract, so the
// compiler never emits a `bs:if` inside another one. Inside a conditional
// block (or a conditional columns block) inner conditions are dropped — the
// outer condition wins — and the wallet picks a single variant.

import {
  COLUMN_RATIOS,
  DEFAULT_BRAND,
  SOCIAL_NETS,
  TEXT_PRESETS,
  blockStatus,
} from './catalog'
import {
  escapeAttr,
  escapeHtml,
  pick,
  safeColor,
  safeFont,
  safeHex,
  safeImgUrl,
  safeNum,
  safeUrl,
  sanitizeInlineHtml,
} from './escape'
import type {
  Block,
  BlockCond,
  CatalogProduct,
  CompileContext,
  EmailCatalog,
  EmailDoc,
  EmailSettings,
} from './types'

// ── Internals ────────────────────────────────────────────────────────────────

interface S {
  brand: string
  text: string
  font: string
  width: number
}

interface X {
  ctx: CompileContext
  s: S
  canvas: boolean
  // True while rendering inside a block that already carries a bs:if marker.
  inCond: boolean
  // The condition carried by that marker (the outer one).
  outerCond?: BlockCond
}

const ALIGNS = ['left', 'center', 'right'] as const
const LINES = ['solid', 'dashed', 'dotted'] as const

const T = (attrs = '') =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"${attrs}>`

// Lighten (a > 0) or darken (a < 0) a hex colour — prototype `shade`.
export function shade(hex: string, a: number): string {
  let h = String(hex || DEFAULT_BRAND).replace('#', '')
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  if (!/^[0-9a-f]{6}/i.test(h)) h = DEFAULT_BRAND.slice(1)
  const n = Number.parseInt(h.slice(0, 6), 16)
  let r = n >> 16
  let g = (n >> 8) & 255
  let b = n & 255
  const t = a < 0 ? 0 : 255
  const p = Math.abs(a)
  r = Math.round(r + (t - r) * p)
  g = Math.round(g + (t - g) * p)
  b = Math.round(b + (t - b) * p)
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

// A link the user actually filled in (not '' and not the bare 'https://').
const isRealLink = (u: unknown) => {
  const s = String(u ?? '').trim()
  return Boolean(s) && !/^https?:\/\/?$/i.test(s)
}

const linkOr = (u: unknown, x: X): string =>
  isRealLink(u) ? safeUrl(u) : safeUrl(x.ctx.storeUrl)

// Canvas only: marks an inline-editable field. `ph` is the empty-field hint
// (`.eb-field[data-ph]:empty::before` in editor.css).
const field = (x: X, name: string, ph = '') =>
  x.canvas
    ? ` data-bs-field="${escapeAttr(name)}"${ph ? ` class="eb-field" data-ph="${escapeAttr(ph)}"` : ''}`
    : ''

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`
const onSale = (p: CatalogProduct) =>
  p.salePrice != null && p.salePrice > 0 && p.salePrice < p.price
const pctOff = (p: CatalogProduct) =>
  Math.round((1 - (p.salePrice as number) / p.price) * 100)

const RULES = ['tier', 'segment', 'member', 'points'] as const

// Only a known mode (only/hide) with a known rule emits a marker; anything
// else is treated as 'all' so a malformed condition never hides a block.
const isConditional = (cond: Partial<BlockCond> | undefined): boolean =>
  Boolean(cond) &&
  (cond?.mode === 'only' || cond?.mode === 'hide') &&
  (RULES as readonly string[]).includes(String(cond?.rule))

const condMarker = (cond: Partial<BlockCond>): string => {
  const mode = cond.mode === 'hide' ? 'hide' : 'only'
  const rule = pick(cond.rule, RULES, 'tier')
  const values = (Array.isArray(cond.values) ? cond.values : [])
    .map((v) => String(v).replace(/[<>]/g, ''))
    .map((v) => (rule === 'tier' ? v.toLowerCase() : v))
  const num =
    rule === 'points' ? Math.max(0, Math.round(safeNum(cond.num, 0))) : 0
  return `<!--bs:if ${JSON.stringify({ mode, rule, values, num })}-->`
}
const END = '<!--bs:endif-->'

const placeholder = (title: string, sub = '', attrs = '') =>
  `<div${attrs} style="background:#F9FAFB;border:1.5px dashed #D1D5DB;border-radius:10px;padding:28px 16px;text-align:center;color:#6B7280;font-size:13px;font-weight:600;line-height:1.5">${escapeHtml(title)}${sub ? `<br><span style="font-size:11.5px;font-weight:400;color:#9CA3AF">${escapeHtml(sub)}</span>` : ''}</div>`

// ── Canvas-only markup ───────────────────────────────────────────────────────
// Editor placeholders and hover states. Never emitted for target 'email';
// their layout (flex, overlays) lives in editor.css classes, not inline.

const CANVAS_IC: Record<string, string> = {
  image:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5-9 9"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  swap: '<path d="M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7"/>',
  coupon:
    '<path d="M3 7.5A1.5 1.5 0 014.5 6h15A1.5 1.5 0 0121 7.5V10a2 2 0 000 4v2.5a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 16.5V14a2 2 0 000-4z"/><path d="M14 6v12" stroke-dasharray="2 2.2"/>',
}
const svg = (name: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${CANVAS_IC[name] ?? ''}</svg>`

// Prototype .eb-img-ph: 170px (180 for Image + text), 28px icon.
const imagePlaceholder = (h: number) =>
  `<div class="eb-img-ph" data-bs-placeholder="image" style="height:${h}px">${svg('image')}<div>Add an image</div><small>Upload in the panel or drop a file here</small></div>`

const emptyMsg = (text: string) =>
  `<div class="eb-empty-msg">${escapeHtml(text)}</div>`

// Bulletproof (table) button.
const buttonHtml = (o: {
  label: string
  href: string
  bg: string
  color: string
  radius: number
  pad: string
  fontSize: number
  full?: boolean
  align?: string
}) =>
  `${T()}<tr><td align="${o.align || 'center'}"><table role="presentation" cellpadding="0" cellspacing="0" border="0"${o.full ? ' width="100%" style="width:100%"' : ''}><tr><td align="center" bgcolor="${o.bg}" style="background:${o.bg};border-radius:${o.radius}px"><a href="${o.href}" style="display:${o.full ? 'block' : 'inline-block'};padding:${o.pad};font-size:${o.fontSize}px;color:${o.color};font-weight:700;text-decoration:none;text-align:center;line-height:1.2;border-radius:${o.radius}px">${o.label}</a></td></tr></table></td></tr></table>`

// Deterministic PRNG (mulberry32) + string hash for per-block seeding.
const mulberry32 = (seed: number) => {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
const hashStr = (s: string) => {
  let h = 2166136261
  for (let i = 0; i < s.length; i++)
    h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return h >>> 0
}

// catalog.all in a deterministic per-block random order.
const shuffled = (b: Block, x: X): CatalogProduct[] => {
  const rnd = mulberry32((x.ctx.seed ?? Date.now()) ^ hashStr(b.id))
  const list = [...(x.ctx.catalog?.all ?? [])]
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[list[i], list[j]] = [list[j], list[i]]
  }
  return list
}

const resolveProducts = (b: Block, x: X): CatalogProduct[] => {
  const p = b.props
  const count = Math.round(safeNum(p.count, 3, 1, 12))
  const cat = x.ctx.catalog
  if (p.source === 'recommended') return []
  if (x.ctx.productFill === 'random') return shuffled(b, x).slice(0, count)
  if (p.source === 'manual') {
    const ids: string[] = Array.isArray(p.ids) ? p.ids : []
    // Optional snapshots (props.items) resolve picks that are outside the
    // loaded catalog page.
    const snaps: CatalogProduct[] = Array.isArray(p.items) ? p.items : []
    return ids
      .map((id) => cat?.byId?.[id] ?? snaps.find((s) => s?.id === id))
      .filter((v): v is CatalogProduct => Boolean(v))
      .slice(0, count)
  }
  const src = pick(p.source, ['best', 'new', 'sale'], 'best')
  const list = (cat?.[src] ?? []).slice(0, count)
  // Test send / final preview: a catalog list that is empty (e.g. no sale
  // items) borrows random products so the test still shows the layout.
  if (!list.length && x.ctx.productFill === 'source-or-random')
    return shuffled(b, x).slice(0, count)
  return list
}

// ── Renderers ────────────────────────────────────────────────────────────────

type R = (b: Block, x: X) => string

const textBlock: R = (b, x) => {
  const p = b.props
  const pr = TEXT_PRESETS[p.preset] || TEXT_PRESETS.p
  const size = safeNum(p.size, pr.size, 8, 96)
  const color = safeColor(p.color, x.s.text)
  const align = pick(p.align, [...ALIGNS, 'justify'], 'left')
  return `<div${field(x, 'html')} style="margin:0;font-size:${size}px;font-weight:${pr.weight};line-height:${pr.lh};color:${color};text-align:${align}">${sanitizeInlineHtml(p.html)}</div>`
}

const imageBlock: R = (b, x) => {
  const p = b.props
  const src = safeImgUrl(p.src)
  if (!src) return x.canvas ? imagePlaceholder(170) : ''
  const w = safeNum(p.width, 100, 10, 100)
  const r = safeNum(p.radius, 0, 0, 999)
  const img = `<img src="${src}" alt="${escapeAttr(p.alt)}" style="display:inline-block;width:${w}%;max-width:100%;height:auto;border-radius:${r}px;vertical-align:top;border:0">`
  return `<div style="text-align:center;line-height:0;font-size:0">${isRealLink(p.link) ? `<a href="${safeUrl(p.link)}">${img}</a>` : img}</div>`
}

const splitBlock: R = (b, x) => {
  const p = b.props
  const br = x.s.brand
  const src = safeImgUrl(p.src)
  const rad =
    p.shape === 'circle' ? '50%' : p.shape === 'rounded' ? '12px' : '0'
  const imgW = Math.round(safeNum(p.imgW, 40, 15, 70))
  const gap = Math.round(safeNum(p.gap, 24, 0, 60))
  const va = p.valign === 'top' ? 'top' : 'middle'
  const align = pick(p.align, ALIGNS, 'left')
  const cls = p.stack !== false ? ' class="bs-col"' : ''

  let pic = ''
  if (src) {
    const img = `<img src="${src}" alt="${escapeAttr(p.alt)}" style="display:block;width:100%;height:auto;border-radius:${rad};border:0">`
    pic = isRealLink(p.link) ? `<a href="${safeUrl(p.link)}">${img}</a>` : img
  } else if (x.canvas) {
    pic = imagePlaceholder(180)
  }

  const tColor = safeColor(p.titleColor, x.s.text)
  const tSize = safeNum(p.titleSize, 20, 10, 60)
  const tSt = `margin:0 0 10px;font-size:${tSize}px;font-weight:800;line-height:1.25;color:${tColor};${p.titleUpper ? 'text-transform:uppercase;letter-spacing:.03em;' : ''}`
  const bSt = `margin:0;font-size:${safeNum(p.bodySize, 15, 10, 30)}px;line-height:1.6;color:${safeColor(p.bodyColor, x.s.text)}`
  const fieldHtml = (k: string, st: string, ph: string) => {
    const html = sanitizeInlineHtml(p[k])
    if (x.canvas) return `<div${field(x, k, ph)} style="${st}">${html}</div>`
    return html.trim() ? `<div style="${st}">${html}</div>` : ''
  }
  const btnBg = safeColor(p.btnBg, br)
  const href = linkOr(p.btnUrl, x)
  let cta = ''
  if (p.showBtn) {
    cta =
      p.btnStyle === 'link'
        ? `<div style="margin-top:14px"><a href="${href}" style="color:${btnBg};font-size:15px;font-weight:700;text-decoration:none">${escapeHtml(p.btnLabel)} &rarr;</a></div>`
        : `<div style="margin-top:18px">${buttonHtml({
            label: escapeHtml(p.btnLabel),
            href,
            bg: btnBg,
            color: safeColor(p.btnColor, '#FFFFFF'),
            radius: safeNum(p.btnRadius, 6, 0, 40),
            pad: '11px 24px',
            fontSize: 14,
            align,
          })}</div>`
  }
  const txt = `${fieldHtml('title', tSt, 'Headline')}${fieldHtml('html', bSt, 'Add a short description')}${cta}`

  if (!pic)
    return `${T()}<tr><td valign="top" style="text-align:${align}">${txt}</td></tr></table>`

  const right = p.side === 'right'
  const imgTd = `<td${cls} width="${imgW}%" valign="${va}" style="width:${imgW}%;${right ? `padding-left:${gap}px` : `padding-right:${gap}px`};line-height:0;font-size:0">${pic}</td>`
  const txtTd = `<td${cls} width="${100 - imgW}%" valign="${va}" style="width:${100 - imgW}%;text-align:${align}">${txt}</td>`
  return `${T()}<tr>${right ? txtTd + imgTd : imgTd + txtTd}</tr></table>`
}

const BTN_SIZES: Record<string, [string, number]> = {
  sm: ['9px 18px', 13],
  md: ['13px 28px', 15],
  lg: ['16px 36px', 17],
}

const buttonBlock: R = (b, x) => {
  const p = b.props
  const [pad, fs] = BTN_SIZES[p.size] || BTN_SIZES.md
  return buttonHtml({
    label: escapeHtml(p.label),
    href: linkOr(p.url, x),
    bg: safeColor(p.bg, x.s.brand),
    color: safeColor(p.color, '#FFFFFF'),
    radius: safeNum(p.radius, 6, 0, 40),
    pad,
    fontSize: fs,
    full: Boolean(p.full),
    align: pick(p.align, ALIGNS, 'center'),
  })
}

const dividerBlock: R = (b) => {
  const p = b.props
  const w = safeNum(p.width, 100, 10, 100)
  return `<table role="presentation" width="${w}%" align="center" cellpadding="0" cellspacing="0" border="0" style="width:${w}%;margin:0 auto"><tr><td style="border-top:${safeNum(p.thick, 1, 1, 10)}px ${pick(p.line, LINES, 'solid')} ${safeColor(p.color, '#E5E7EB')};height:0;line-height:0;font-size:0">&nbsp;</td></tr></table>`
}

const spacerBlock: R = (b, x) => {
  const h = safeNum(b.props.h, 32, 4, 200)
  return `<div${x.canvas ? ' class="eb-spacer"' : ''} style="height:${h}px;line-height:${h}px;font-size:0">&nbsp;</div>`
}

const tableBlock: R = (b, x) => {
  const p = b.props
  const br = x.s.brand
  const border = safeColor(p.border, '#E5E7EB')
  const rows: unknown[][] = Array.isArray(p.rows) ? p.rows : []
  const body = rows
    .map((r, ri) => {
      const head = Boolean(p.header) && ri === 0
      const bi = ri - (p.header ? 1 : 0)
      const bg = head
        ? shade(br, 0.88)
        : p.striped && bi % 2 === 1
          ? '#F9FAFB'
          : '#FFFFFF'
      const cells = (Array.isArray(r) ? r : [])
        .map(
          (c, ci) =>
            `<td${x.canvas ? ` data-bs-cell="${ri}:${ci}"` : ''} style="border:1px solid ${border};padding:9px 11px;background:${bg};${head ? `font-weight:700;color:${shade(br, -0.4)};` : ''}text-align:left;vertical-align:top">${escapeHtml(c)}</td>`,
        )
        .join('')
      return `<tr>${cells}</tr>`
    })
    .join('')
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;font-size:${safeNum(p.size, 13, 10, 20)}px;color:${x.s.text}">${body}</table>`
}

// Social icons: one table cell per network, each a 34px circle (filled with
// the icon colour, or outlined in it) holding the network's PNG glyph from
// the API (`/public/loyalty/email/social/<net>.png?color=`) — email clients
// show neither SVG nor data: images. A network with no link is left out of
// the email; the canvas still shows it, faded, so the author can see it.
const socialBlock: R = (b, x) => {
  const p = b.props
  const nets = p.nets || {}
  const urls = p.urls || {}
  const on = Object.keys(SOCIAL_NETS).filter(
    (k) => nets[k] && (x.canvas || isRealLink(urls[k])),
  )
  if (!on.length)
    return x.canvas ? emptyMsg('Turn on at least one network in the panel') : ''
  const color = safeHex(safeColor(p.color, x.s.brand), x.s.brand)
  const solid = p.shape !== 'outline'
  const half = Math.round(safeNum(p.gap, 8, 0, 40) / 2)
  const base = String(x.ctx.assetBase ?? '').replace(/\/+$/, '')
  const glyphHex = (solid ? '#FFFFFF' : color).slice(1)
  const cells = on
    .map((k) => {
      const [label, glyph] = SOCIAL_NETS[k]
      const linked = isRealLink(urls[k])
      const href = linked ? safeUrl(urls[k]) : '#'
      const inner = base
        ? `<img src="${escapeAttr(`${base}/public/loyalty/email/social/${k}.png?color=${glyphHex}`)}" width="18" height="18" alt="${label}" style="display:block;width:18px;height:18px;border:0">`
        : `<span style="font-size:12px;font-weight:800;line-height:18px;color:#${glyphHex}">${glyph}</span>`
      const circle = solid
        ? `width="34" height="34" bgcolor="${color}" style="width:34px;height:34px;border-radius:50%;background:${color}"`
        : `width="31" height="31" style="width:31px;height:31px;border-radius:50%;border:1.5px solid ${color}"`
      const faded = linked ? '' : ';opacity:.35'
      return `<td style="padding:0 ${half}px${faded}"${linked ? '' : ' title="Add a link in the panel — left out of the email until then"'}><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" ${circle}><a href="${href}" title="${label}" style="display:inline-block;text-decoration:none">${inner}</a></td></tr></table></td>`
    })
    .join('')
  const align = pick(p.align, ALIGNS, 'center')
  return `<table role="presentation" align="${align}" cellpadding="0" cellspacing="0" border="0"${align === 'center' ? ' style="margin:0 auto"' : ''}><tr>${cells}</tr></table>`
}

const headerBlock: R = (b, x) => {
  const p = b.props
  let links: Array<{ label: string; url: string }> = []
  if (typeof p.links === 'string')
    links = p.links
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean)
      .map((label: string) => ({ label, url: '' }))
  else if (Array.isArray(p.links)) links = p.links
  const imgSrc =
    p.logoMode === 'text'
      ? ''
      : safeImgUrl(p.logoImg) || safeImgUrl(x.ctx.brandLogoUrl)
  const spacing = /^-?[0-9.]+(em|px)?$/.test(String(p.logoSpacing ?? ''))
    ? p.logoSpacing
    : '0'
  const logoHtml = sanitizeInlineHtml(
    p.logoHtml ?? `<b>${escapeHtml(p.logoText)}</b>`,
  )
  const logo = imgSrc
    ? `<img src="${imgSrc}" alt="${escapeAttr(String(p.logoAlt ?? '').trim() || p.logoText || x.ctx.storeName)}" style="max-height:44px;max-width:200px;display:inline-block;border:0">`
    : `<div${field(x, 'logoHtml')} style="font-family:${safeFont(p.logoFont, x.s.font)};font-size:${safeNum(p.logoSize, 22, 10, 60)}px;line-height:1.2;letter-spacing:${spacing};color:${safeColor(p.logoColor, x.s.brand)}">${logoHtml || '&nbsp;'}</div>`
  const items = links.filter((l) => String(l?.label ?? '').trim())
  const nav =
    p.showLinks && items.length
      ? `<div style="margin-top:12px;font-size:${safeNum(p.linkSize, 13, 10, 24)}px;line-height:1.8">${items
          .map(
            (l) =>
              `<a href="${linkOr(l.url, x)}" style="color:${safeColor(p.linkColor, x.s.text)};text-decoration:none;margin:0 9px;font-weight:500;white-space:nowrap">${escapeHtml(l.label)}</a>`,
          )
          .join('')}</div>`
      : ''
  return `<div style="text-align:${pick(p.align, ALIGNS, 'center')}">${logo}${nav}</div>`
}

const footerBlock: R = (b, x) => {
  const p = b.props
  const color = safeColor(p.color, '#6B7280')
  const st = `font-size:${safeNum(p.size, 12, 9, 20)}px;line-height:1.6;color:${color};text-align:${pick(p.align, ALIGNS, 'center')}`
  return `<div${field(x, 'html')} style="${st}">${sanitizeInlineHtml(p.html)}</div><div style="${st};margin-top:12px"><a href="{{unsubscribe_url}}" style="color:${color};text-decoration:underline">Unsubscribe</a></div>`
}

const titleHtml = (b: Block, x: X) => {
  const p = b.props
  if (!p.showTitle) return ''
  const pr = TEXT_PRESETS[p.titlePreset] || TEXT_PRESETS.h2
  const st = `margin:0 0 14px;font-size:${safeNum(p.titleSize, pr.size, 10, 60)}px;font-weight:${pr.weight};line-height:${pr.lh};color:${safeColor(p.titleColor, x.s.text)};text-align:${pick(p.titleAlign, ALIGNS, 'center')}`
  const html = sanitizeInlineHtml(p.title)
  if (x.canvas)
    return `<div${field(x, 'title', 'Add a heading')} style="${st}">${html}</div>`
  return html.trim() ? `<div style="${st}">${html}</div>` : ''
}

const productBlock: R = (b, x) => {
  const p = b.props
  const t = x.s.text
  const br = x.s.brand
  if (p.source === 'recommended')
    return x.canvas
      ? placeholder(
          'Recommended products are coming soon',
          'This block is left out of the email',
        )
      : ''
  const items = resolveProducts(b, x)
  const per = Math.round(safeNum(p.perRow, 3, 1, 4))
  const manual = p.source === 'manual' && x.ctx.productFill !== 'random'
  const slots =
    x.canvas && manual
      ? Math.max(0, Math.round(safeNum(p.count, 3, 1, 12)) - items.length)
      : 0
  const title = titleHtml(b, x)
  if (!items.length && !slots)
    return x.canvas
      ? title +
          placeholder(
            'No products to show yet',
            'Products appear once your catalog loads',
          )
      : ''

  const pickingSlot =
    x.canvas && manual && x.ctx.pick?.id === b.id ? x.ctx.pick.slot : -1
  const card = (it: CatalogProduct, i: number) => {
    const href = isRealLink(it.url) ? safeUrl(it.url) : safeUrl(x.ctx.storeUrl)
    const src = safeImgUrl(it.image)
    // Sale badge: its own row tight above the image, left-aligned (an overlay
    // on the image needs positioning, which email clients don't support).
    const sale = p.saleBadge !== false && onSale(it)
    const imgRadius = sale ? '0' : '10px 10px 0 0'
    const img = src
      ? `<a href="${href}"><img src="${src}" alt="${escapeAttr(it.name)}" style="display:block;width:100%;height:auto;border:0;border-radius:${imgRadius}"></a>`
      : `<div style="height:140px;background:#F3F4F6;border-radius:${imgRadius}">&nbsp;</div>`
    const badge = sale
      ? `<tr><td align="left" style="padding:8px 8px 6px;line-height:1;text-align:left"><span style="display:inline-block;background:#DC2626;color:#FFFFFF;font-size:11px;font-weight:800;line-height:1;padding:5px 8px;border-radius:999px;letter-spacing:.02em">SALE ${pctOff(it)}% OFF</span></td></tr>`
      : ''
    const metaTxt = [it.size, it.meta]
      .filter((v) => String(v ?? '').trim())
      .join(' · ')
    const meta =
      p.showMeta && metaTxt
        ? `<div style="font-size:12px;color:#6B7280;margin-top:3px">${escapeHtml(metaTxt)}</div>`
        : ''
    const price = !p.showPrice
      ? ''
      : onSale(it)
        ? `<div style="margin-top:6px;line-height:1.3"><span style="font-size:15px;font-weight:800;color:#DC2626">${money(it.salePrice as number)}</span> <span style="font-size:12.5px;color:#9CA3AF;text-decoration:line-through">${money(it.price)}</span></div>`
        : `<div style="font-size:15px;font-weight:800;color:${t};margin-top:6px">${money(it.price)}</div>`
    // The card fills its grid cell (cells in a row share a height) and the
    // details row absorbs the spare space, so every button in a row sits on
    // the same bottom line however long the names are. Clients that ignore
    // percentage heights (Outlook desktop) just show the natural height.
    const btn = p.showBtn
      ? `<tr><td valign="bottom" style="vertical-align:bottom;padding:10px 12px 12px"><a href="${href}" style="display:block;background:${br};color:#FFFFFF;text-align:center;padding:8px 10px;border-radius:6px;font-size:13px;font-weight:700;text-decoration:none">${escapeHtml(p.btnLabel)}</a></td></tr>`
      : ''
    const infoPad = p.showBtn ? '10px 12px 0' : '10px 12px 12px'
    const html = `<table role="presentation" width="100%" height="100%" cellpadding="0" cellspacing="0" border="0" style="height:100%;border:1px solid #E5E7EB;border-radius:10px;background:#FFFFFF;border-collapse:separate">${badge}<tr><td style="line-height:0;font-size:0">${img}</td></tr><tr><td valign="top" style="vertical-align:top;height:100%;padding:${infoPad}"><div style="font-size:14px;font-weight:700;color:${t};line-height:1.3">${escapeHtml(it.name)}</div>${meta}${price}</td></tr>${btn}</table>`
    if (!(x.canvas && manual)) return html
    // Canvas, "Products I choose": hover pill to swap the product.
    return `<div class="eb-prod-card${i === pickingSlot ? ' picking' : ''}" data-bs-slot="${i}" style="height:100%" title="Select a different product for this slot">${html}<span class="eb-prod-sel"><span>${svg('swap')}Select product</span></span></div>`
  }
  const slot = (i: number) =>
    `<div class="eb-prod-slot${i === pickingSlot ? ' picking' : ''}" data-bs-slot="${i}" title="Select a product for this slot">${svg('plus')}<span>Select product</span></div>`

  const cells = [
    ...items.map(card),
    ...Array.from({ length: slots }, (_, k) => slot(items.length + k)),
  ]
  return `${title}${productGrid(cells, per)}`
}

// Product grid with 2 per row on phones, using only the backend wrapper's
// `.bs-col` / `.bs-hide-m` rules (no flex / grid / position):
//   1 or 2 per row: one plain grid (never stacks).
//   3 or 4 per row: the desktop grid sits in a `.bs-hide-m` wrapper, and a
//   phone grid of pairs sits in a `.bs-col` wrapper that is display:none
//   until the wrapper's media rule (`.bs-col{display:block!important}`)
//   reveals it on phones. Clients without media queries show the desktop
//   grid only.
function gridTable(cells: string[], per: number): string {
  const w = Math.floor(100 / per)
  const rows: string[] = []
  for (let i = 0; i < cells.length; i += per) {
    const row = cells.slice(i, i + per)
    const gap = i + per < cells.length ? ' 12px' : ''
    const tds = row.map(
      (c) =>
        // height:1px lets the card's height:100% resolve to the row height.
        `<td width="${w}%" valign="top" style="width:${w}%;height:1px;padding:0 6px${gap}">${c}</td>`,
    )
    for (let k = row.length; k < per; k++)
      tds.push(
        `<td width="${w}%" style="width:${w}%;padding:0 6px">&nbsp;</td>`,
      )
    rows.push(`<tr>${tds.join('')}</tr>`)
  }
  return `${T()}${rows.join('')}</table>`
}

function productGrid(cells: string[], per: number): string {
  if (per <= 2) return gridTable(cells, per)
  return `<div class="bs-hide-m">${gridTable(cells, per)}</div><div class="bs-col" style="display:none;mso-hide:all">${gridTable(cells, 2)}</div>`
}

const couponBlock: R = (b, x) => {
  const p = b.props
  const code = String(p.code ?? '').trim()
  if (!code)
    return x.canvas
      ? `<div class="eb-coupon-ph" data-bs-placeholder="coupon">${svg('coupon')}<div>Select a coupon</div><small>Pulls the code and discount from your Coupons module</small></div>`
      : ''
  const br = x.s.brand
  const c = (x.ctx.coupons || []).find(
    (k) => String(k.code).toLowerCase() === code.toLowerCase(),
  )
  // `prefilled` blocks got their wording copied from the coupon when it was
  // picked, so an emptied field stays empty. Older blocks (and starters)
  // fall back to the coupon's own title / description.
  const title = String(
    p.prefilled ? (p.title ?? '') : p.title || c?.title || '',
  ).trim()
  const note = String(
    p.prefilled ? (p.note ?? '') : p.note || c?.description || '',
  ).trim()
  const scope = p.showScope !== false ? String(p.scopeText ?? '').trim() : ''
  return `${T()}<tr><td align="center" style="border:2px ${pick(p.line, LINES, 'dashed')} ${br};border-radius:12px;background:${shade(br, 0.93)};padding:18px 20px;text-align:center">${
    title
      ? `<div style="font-size:15px;font-weight:700;color:${x.s.text}">${escapeHtml(title)}</div>`
      : ''
  }<div style="font-family:'Courier New',Courier,monospace;font-size:26px;font-weight:800;letter-spacing:.14em;color:${br};margin:8px 0 6px">${escapeHtml(code)}</div>${
    note
      ? `<div style="font-size:12px;color:#6B7280">${escapeHtml(note)}</div>`
      : ''
  }${
    scope
      ? `<div style="font-size:11.5px;color:#6B7280;margin-top:6px;font-weight:600">${escapeHtml(scope)}</div>`
      : ''
  }</td></tr></table>`
}

const BARCODE = '211214121132213121311214123112312131141112313'
const barcode = () =>
  `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>${BARCODE.split(
    '',
  )
    .map((d, i) => {
      const w = Number(d) * 2
      return `<td width="${w}" style="width:${w}px;height:40px;background:${i % 2 ? '#FFFFFF' : '#111827'};font-size:0;line-height:0">&nbsp;</td>`
    })
    .join('')}</tr></table>`

export const DEFAULT_JOIN_COPY =
  'Earn points every time you shop and unlock rewards.'

const walletBlock: R = (b, x) => {
  const p = b.props
  const br = safeHex(p.color, x.s.brand)
  const dark = shade(br, -0.4)
  const store = escapeHtml(x.ctx.storeName) || '{{store_name}}'
  // Canvas with a "Preview as" sample: show that customer's values (the
  // email keeps the tokens and markers; the backend fills them per customer).
  const sample = x.canvas ? x.ctx.sample : undefined
  const v = (k: string) =>
    sample ? escapeHtml(sample.vals[k] ?? '') : `{{${k}}}`
  const pct = sample
    ? String(Math.round(safeNum(sample.vals.tier_progress_pct, 0, 0, 100)))
    : x.canvas
      ? '60'
      : '{{tier_progress_pct}}'
  // The backend leaves next_tier / points_to_next_tier empty at the top tier
  // (and the top tier depends on which tiers the store configured), so the
  // email never prints a "points to <next tier>" line: the bar (100% at the
  // top) and the balance read correctly for every member. The reward row is
  // never emitted — the backend has no reward value to fill.
  const progress = p.progress
    ? `${T(' style="margin-top:14px"')}<tr><td style="height:6px;border-radius:3px;background:rgba(255,255,255,.22);font-size:0;line-height:0"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${pct}%" style="width:${pct}%"><tr><td style="height:6px;border-radius:3px;background:#FFFFFF;font-size:0;line-height:0">&nbsp;</td></tr></table></td></tr></table><div style="font-size:12px;color:#FFFFFF;opacity:.88;margin-top:6px">${v('points_balance')} points</div>`
    : ''
  const code = p.barcode
    ? `${T(' style="margin-top:14px"')}<tr><td align="center" style="background:#FFFFFF;border-radius:10px;padding:10px 12px 8px;text-align:center">${barcode()}<div style="font-family:'Courier New',Courier,monospace;font-size:11px;color:#374151;margin-top:5px;letter-spacing:.12em">${v('member_id')}</div></td></tr></table>`
    : ''
  const card = `${T(' style="border-collapse:separate"')}<tr><td bgcolor="${br}" style="background-color:${br};background-image:linear-gradient(135deg,${br},${dark});border-radius:16px;padding:22px 22px 18px;color:#FFFFFF">${T()}<tr><td style="font-size:14px;font-weight:700;color:#FFFFFF">${store} Rewards</td><td align="right" style="text-align:right"><span style="background:rgba(255,255,255,.18);border-radius:20px;padding:3px 11px;font-size:12px;font-weight:700;white-space:nowrap;color:#FFFFFF">&#9733; ${v('tier_name')}</span></td></tr></table><div style="font-size:12px;color:#FFFFFF;opacity:.75;margin-top:18px">Points balance</div><div style="font-size:38px;font-weight:800;line-height:1.1;color:#FFFFFF">${v('points_balance')}</div>${progress}${code}</td></tr></table>`
  // Join copy: the store's real earn / redemption rules when the editor
  // captured them (props.joinText), else a generic line.
  const joinCopy = String(p.joinText ?? '').trim() || DEFAULT_JOIN_COPY
  const join = `${T()}<tr><td style="border:1.5px solid #E5E7EB;border-radius:16px;padding:24px 22px;text-align:center;background:#FFFFFF"><div style="font-size:13px;font-weight:700;color:${br}">${store} Rewards</div><div style="font-size:20px;font-weight:800;color:${x.s.text};margin-top:6px">Join free and start earning</div><div style="font-size:13px;color:#6B7280;margin-top:6px;line-height:1.5">${escapeHtml(joinCopy)}</div><div style="margin-top:14px">${buttonHtml({ label: 'Join rewards', href: linkOr(p.joinUrl, x), bg: br, color: '#FFFFFF', radius: 8, pad: '11px 24px', fontSize: 14 })}</div></td></tr></table>`

  const showJoin = p.guest !== 'hide'
  if (x.canvas) {
    if (!sample || sample.member) return card
    return showJoin
      ? join
      : emptyMsg('Hidden for customers who aren’t loyalty members')
  }
  if (x.inCond) {
    // Can't nest markers: pick one variant from the outer condition.
    const c = x.outerCond ?? b.cond
    const outerExcludesMembers = c.rule === 'member' && c.mode === 'hide'
    if (outerExcludesMembers) return showJoin ? join : ''
    return card
  }
  const member = condMarker({
    mode: 'only',
    rule: 'member',
    values: [],
    num: 0,
  })
  const guest = condMarker({ mode: 'hide', rule: 'member', values: [], num: 0 })
  return `${showJoin ? `${guest}${join}${END}` : ''}${member}${card}${END}`
}

const cartBlock: R = (b, x) => {
  const p = b.props
  const t = x.s.text
  const showImg = p.showImage !== false
  const img = x.canvas
    ? '<div style="width:64px;height:64px;background:#F3F4F6;border-radius:8px">&nbsp;</div>'
    : '<img src="{{item_image}}" alt="{{item_name}}" width="64" style="display:block;width:64px;height:auto;border:0;border-radius:8px">'
  const row = `${T(' style="border:1px solid #E5E7EB;border-radius:10px;background:#FFFFFF;border-collapse:separate;margin-bottom:10px"')}<tr>${showImg ? `<td width="74" valign="middle" style="width:74px;padding:10px 0 10px 10px">${img}</td>` : ''}<td valign="middle" style="padding:10px 12px"><div style="font-size:14px;font-weight:700;color:${t};line-height:1.3">{{item_name}}</div>${
    p.showMeta !== false
      ? '<div style="font-size:12px;color:#6B7280;margin-top:3px">{{item_variant}}</div><div style="font-size:12px;color:#6B7280;margin-top:2px">Qty {{item_qty}}</div>'
      : ''
  }</td>${
    p.showPrice !== false
      ? `<td align="right" valign="middle" style="padding:10px 12px;font-size:15px;font-weight:800;color:${t};white-space:nowrap;text-align:right">{{item_price}}</td>`
      : ''
  }</tr></table>`
  const fallback = buttonHtml({
    label: 'View your cart',
    href: '{{cart_url}}',
    bg: x.s.brand,
    color: '#FFFFFF',
    radius: 6,
    pad: '13px 28px',
    fontSize: 15,
  })
  const title = titleHtml(b, x)
  // Only abandoned-cart sends carry cart lines; every other email gets the
  // fallback button, so render exactly that.
  if (x.ctx.cartItems === false) return title + fallback
  if (x.canvas) {
    const sample = x.ctx.sample
    if (!sample) return title + row
    // The "Preview as" customer's cart, or the fallback for an empty cart.
    if (!sample.cart.length) return title + fallback
    return (
      title +
      sample.cart
        .map((it) => {
          const src = safeImgUrl(it.image)
          return row
            .replace(img, () =>
              src
                ? `<img src="${src}" alt="" width="64" style="display:block;width:64px;height:auto;border:0;border-radius:8px">`
                : img,
            )
            .replace('{{item_name}}', () => escapeHtml(it.name))
            .replace('{{item_variant}}', () => escapeHtml(it.variant))
            .replace('{{item_qty}}', '1')
            .replace('{{item_price}}', () => money(it.price))
        })
        .join('')
    )
  }
  return `${title}<!--bs:cart_items-->${row}<!--bs:cart_fallback-->${fallback}<!--bs:/cart_items-->`
}

const columnsBlock: R = (b, x) => {
  const p = b.props
  const ratio = pick(p.ratio, COLUMN_RATIOS, '50-50')
  const ws = ratio.split('-').map((n) => safeNum(n, 50, 1, 100))
  const cols: Block[][] = Array.isArray(p.cols) ? p.cols : []
  const gap = Math.round(safeNum(p.gap, 16, 0, 60))
  const half = Math.round(gap / 2)
  const cls = p.stack !== false ? ' class="bs-col"' : ''
  const n = Math.max(ws.length, 1)
  const tds = ws.map((w, i) => {
    const list = cols[i] ?? []
    const inner = list
      .map((child) => (child.type === 'columns' ? '' : renderBlock(child, x)))
      .join('')
    const pl = i === 0 ? 0 : half
    const pr = i === n - 1 ? 0 : half
    const body =
      inner ||
      (x.canvas ? '<div style="min-height:40px">&nbsp;</div>' : '&nbsp;')
    return `<td${cls}${x.canvas ? ` data-bs-col="${i}"` : ''} width="${w}%" valign="top" style="width:${w}%;padding:0 ${pr}px 0 ${pl}px">${body}</td>`
  })
  return `${T()}<tr>${tds.join('')}</tr></table>`
}

const soonBlock: R = (_b, x) =>
  x.canvas
    ? placeholder('This block is coming soon', 'It is left out of the email')
    : ''

const RENDERERS: Record<string, R> = {
  columns: columnsBlock,
  text: textBlock,
  image: imageBlock,
  split: splitBlock,
  button: buttonBlock,
  divider: dividerBlock,
  spacer: spacerBlock,
  table: tableBlock,
  social: socialBlock,
  header: headerBlock,
  footer: footerBlock,
  product: productBlock,
  coupon: couponBlock,
  wallet: walletBlock,
  cart: cartBlock,
}

function renderBlock(block: Block, x: X): string {
  const status = blockStatus(block)
  const r = status === 'ready' ? RENDERERS[block.type] : soonBlock
  const cond = block.cond
  const conditional = isConditional(cond)
  const emitMarker = !x.canvas && conditional && !x.inCond
  const inner = (r ?? soonBlock)(block, {
    ...x,
    inCond: x.inCond || emitMarker,
    outerCond: emitMarker ? cond : x.outerCond,
  })
  if (!inner) return ''
  const st = block.style || { padT: 0, padB: 0, padX: 0, bg: '' }
  const pt = safeNum(st.padT, 0, 0, 200)
  const pb = safeNum(st.padB, 0, 0, 200)
  const px = safeNum(st.padX, 0, 0, 200)
  const bg = safeColor(st.bg, 'transparent')
  const html = `${T()}<tr><td style="padding:${pt}px ${px}px ${pb}px;background:${bg}">${inner}</td></tr></table>`
  if (x.canvas) return `<div data-bs-id="${escapeAttr(block.id)}">${html}</div>`
  return emitMarker ? `${condMarker(cond)}${html}${END}` : html
}

const resolveSettings = (settings: EmailSettings): S => ({
  brand: safeHex(settings?.brand, DEFAULT_BRAND),
  text: safeColor(settings?.text, '#1F2937'),
  font: safeFont(settings?.font, 'Helvetica, Arial, sans-serif'),
  width: safeNum(settings?.width, 600, 320, 900),
})

// Product blocks (best / new / sale / manual) that resolve to no products with
// this catalog — the email would render them empty. Recommended is
// per-recipient so it is never counted. `manualOnly` restricts the count to
// manual-pick blocks, whose `props.items` snapshots resolve without the
// catalog — the honest count while the catalog is still loading (judging a
// catalog-sourced block against an unloaded catalog would flag it falsely).
export function countEmptyProductBlocks(
  blocks: Block[],
  catalog: EmailCatalog,
  opts: { manualOnly?: boolean } = {},
): number {
  const x = { ctx: { catalog } } as unknown as X
  const walk = (list: Block[]): Block[] =>
    (list || []).flatMap((b) =>
      b.type === 'columns' && Array.isArray(b.props?.cols)
        ? walk((b.props.cols as Block[][]).flat())
        : [b],
    )
  return walk(blocks).filter(
    (b) =>
      b.type === 'product' &&
      b.props?.source !== 'recommended' &&
      (!opts.manualOnly || b.props?.source === 'manual') &&
      resolveProducts(b, x).length === 0,
  ).length
}

// Product blocks filled from the store catalog at compile time (best sellers
// / new arrivals / on sale — anything but manual picks and recommended):
// compiling them before the catalog has loaded drops their products.
export function hasCatalogProductBlock(blocks: Block[]): boolean {
  return (blocks || []).some((b) =>
    b.type === 'columns' && Array.isArray(b.props?.cols)
      ? hasCatalogProductBlock((b.props.cols as Block[][]).flat())
      : b.type === 'product' &&
        b.props?.source !== 'manual' &&
        b.props?.source !== 'recommended',
  )
}

// ── Public API ───────────────────────────────────────────────────────────────

export function compileBlock(
  block: Block,
  settings: EmailSettings,
  ctx: CompileContext,
): string {
  return renderBlock(block, {
    ctx,
    s: resolveSettings(settings),
    canvas: ctx.target === 'canvas',
    inCond: false,
  })
}

// Body HTML only (the backend wraps it — see wrapPreviewDocument).
export function compileEmail(doc: EmailDoc, ctx: CompileContext): string {
  return (doc.blocks || [])
    .map((b) => compileBlock(b, doc.settings, ctx))
    .join('')
}

// Same document wrapper the backend applies (B2), for preview / canvas iframes.
export function wrapPreviewDocument(
  bodyHtml: string,
  settings: EmailSettings,
  preheader: string,
): string {
  const bg = safeColor(settings?.bg, '#F3F4F6')
  const body = safeColor(settings?.body, '#FFFFFF')
  const s = resolveSettings(settings)
  const width = Math.round(s.width)
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>@media (max-width:620px){.bs-col{display:block!important;width:100%!important}.bs-hide-m{display:none!important}}</style></head>
<body style="margin:0;padding:0;background:${bg}"><div style="display:none;max-height:0;overflow:hidden">${escapeHtml(preheader)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${bg}"><tr><td align="center" style="padding:24px 12px">
<table role="presentation" width="${width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${width}px;background:${body};font-family:${s.font};color:${s.text}"><tr><td>
${bodyHtml}
</td></tr></table></td></tr></table></body></html>`
}
